/**
 * Credit & Monetization Service
 * 
 * This service manages the freemium model WITHOUT breaking auth or app functionality.
 * 
 * KEY PRINCIPLE: Credit checks are READ-ONLY operations that happen BEFORE expensive actions.
 * They NEVER modify user state, sign users out, or break the app.
 * 
 * FREEMIUM MODEL:
 * - Anonymous users: 3 free try-ons (localStorage) for main app
 * - Onboarding: 1 free try (handled separately via guestUsageService)
 * - Signed-in free users: 10 try-ons per month
 * - Pro subscribers: Unlimited try-ons
 */

import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

// --------------------------------------------------
// CONFIGURATION
// --------------------------------------------------

export const CREDIT_CONFIG = {
  // Anonymous users (not logged in) - for Explore, extension, etc.
  ANONYMOUS_FREE_TRIES: 3,
  
  // Authenticated free users
  FREE_USER_MONTHLY_TRIES: 10,
  
  // Pro subscription
  PRO_UNLIMITED: true,
  
  // Admin mode
  ADMIN_UNLIMITED: true,
} as const;

const ANON_TRY_KEY = 'garu_anon_tries';

// --------------------------------------------------
// TYPES
// --------------------------------------------------

export type UserTier = 'anonymous' | 'free' | 'pro' | 'admin';

export interface CreditStatus {
  tier: UserTier;
  hasCredits: boolean;
  triesRemaining: number | 'unlimited';
  triesUsed: number;
  needsUpgrade: boolean;
  upgradeMessage: string;
}

// --------------------------------------------------
// MAIN CREDIT CHECK FUNCTION
// --------------------------------------------------

/**
 * Check if user has credits to perform an action
 * This is a READ-ONLY operation that NEVER breaks the app
 */
export async function checkCredits(user: User | null, adminModeEnabled: boolean = true): Promise<CreditStatus> {
  try {
    // 1. ANONYMOUS USERS (not logged in)
    if (!user) {
      return checkAnonymousCredits();
    }

    // 2. CHECK IF ADMIN ROLE and AdminMode is enabled (unlimited access only when Admin Mode is ON)
    const isAdmin = await isUserAdmin(user.id);
    if (isAdmin && adminModeEnabled) {
      return {
        tier: 'admin',
        hasCredits: true,
        triesRemaining: 'unlimited',
        triesUsed: 0,
        needsUpgrade: false,
        upgradeMessage: ''
      };
    }

    // 3. CHECK IF PRO SUBSCRIBER (unlimited access)
    const isPro = await isUserProSubscriber(user.id);
    if (isPro) {
      return {
        tier: 'pro',
        hasCredits: true,
        triesRemaining: 'unlimited',
        triesUsed: 0,
        needsUpgrade: false,
        upgradeMessage: ''
      };
    }

    // 4. FREE USER (limited tries)
    return await checkFreeUserCredits(user.id);

  } catch (error) {
    console.error('[CreditService] Error checking credits:', error);
    
    // FAIL OPEN: If credit check fails, allow the action
    // This prevents breaking the app if database is slow/down
    return {
      tier: 'free',
      hasCredits: true,
      triesRemaining: 1,
      triesUsed: 0,
      needsUpgrade: false,
      upgradeMessage: 'Credit check temporarily unavailable'
    };
  }
}

// --------------------------------------------------
// ANONYMOUS USER LOGIC
// --------------------------------------------------

function checkAnonymousCredits(): CreditStatus {
  const used = getAnonymousTries();
  const remaining = Math.max(0, CREDIT_CONFIG.ANONYMOUS_FREE_TRIES - used);
  
  return {
    tier: 'anonymous',
    hasCredits: remaining > 0,
    triesRemaining: remaining,
    triesUsed: used,
    needsUpgrade: remaining === 0,
    upgradeMessage: remaining === 0 
      ? 'Sign up for 10 free try-ons per month!' 
      : `${remaining} free ${remaining === 1 ? 'try' : 'tries'} remaining`
  };
}

function getAnonymousTries(): number {
  try {
    const stored = localStorage.getItem(ANON_TRY_KEY);
    return stored ? parseInt(stored, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

/**
 * Increment anonymous try counter
 * Call this AFTER successful try-on
 */
export function incrementAnonymousTries(): void {
  try {
    const current = getAnonymousTries();
    localStorage.setItem(ANON_TRY_KEY, (current + 1).toString());
  } catch (error) {
    console.error('[CreditService] Failed to increment anonymous tries:', error);
  }
}

/**
 * Reset anonymous tries (called after sign-up)
 */
export function resetAnonymousTries(): void {
  try {
    localStorage.removeItem(ANON_TRY_KEY);
  } catch {
    // Ignore errors
  }
}

// --------------------------------------------------
// FREE USER LOGIC
// --------------------------------------------------

async function checkFreeUserCredits(userId: string): Promise<CreditStatus> {
  // Get user's remaining try-on credits from user_credits table
  const { data: credits, error } = await supabase
    .from('user_credits')
    .select('tryon_credits')
    .eq('user_id', userId)
    .single();

  if (error || !credits) {
    // Fail open: If we can't check, allow the action
    return {
      tier: 'free',
      hasCredits: true,
      triesRemaining: 1,
      triesUsed: 0,
      needsUpgrade: false,
      upgradeMessage: ''
    };
  }

  // tryon_credits is the REMAINING credits
  const remaining = credits.tryon_credits || 0;
  const used = CREDIT_CONFIG.FREE_USER_MONTHLY_TRIES - remaining;

  return {
    tier: 'free',
    hasCredits: remaining > 0,
    triesRemaining: remaining,
    triesUsed: Math.max(0, used),
    needsUpgrade: remaining === 0,
    upgradeMessage: remaining === 0
      ? 'Upgrade to Pro for unlimited try-ons!'
      : `${remaining} ${remaining === 1 ? 'try' : 'tries'} remaining this month`
  };
}

/**
 * Decrement user's credits after successful try-on
 * Call this AFTER the try-on succeeds
 */
export async function decrementUserCredits(userId: string): Promise<boolean> {
  try {
    console.log('💳 [CreditService] Decrementing credits for user:', userId);
    
    const { data, error } = await supabase.rpc('decrement_tryon_credit', {
      user_id_param: userId
    });

    if (error) {
      console.error('❌ [CreditService] Failed to decrement credits:', error);
      console.error('   Error details:', {
        message: error.message,
        code: error.code,
        hint: error.hint
      });
      return false;
    }

    console.log('✅ [CreditService] Credits decremented successfully:', data);
    return true;
  } catch (error) {
    console.error('❌ [CreditService] Exception decrementing credits:', error);
    return false;
  }
}

// --------------------------------------------------
// SUBSCRIPTION CHECKS
// --------------------------------------------------

async function isUserProSubscriber(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('subscription_status')
      .eq('user_id', userId)
      .single();

    if (error || !data) return false;

    // Check for active subscription
    return data.subscription_status === 'active' || 
           data.subscription_status === 'trialing';
  } catch {
    return false;
  }
}

async function isUserAdmin(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle();

    return !error && !!data;
  } catch {
    return false;
  }
}

// --------------------------------------------------
// HELPER FUNCTIONS
// --------------------------------------------------

/**
 * Get user-friendly message for upgrade prompt
 */
export function getUpgradeMessage(status: CreditStatus): string {
  if (status.tier === 'anonymous') {
    return 'Sign up to get 10 free try-ons per month!';
  }
  
  if (status.tier === 'free' && status.needsUpgrade) {
    return 'Upgrade to Pro for unlimited try-ons. Just $9.99/month!';
  }

  if (typeof status.triesRemaining === 'number') {
    return `${status.triesRemaining} ${status.triesRemaining === 1 ? 'try' : 'tries'} remaining`;
  }

  return '';
}

/**
 * Get CTA button text based on user status
 */
export function getUpgradeCTA(status: CreditStatus): string {
  if (status.tier === 'anonymous') {
    return 'Sign Up Free';
  }
  
  if (status.tier === 'free') {
    return 'Upgrade to Pro';
  }

  return 'Continue';
}

/**
 * Reset monthly credits (called by cron job or manually)
 */
export async function resetMonthlyCredits(): Promise<void> {
  try {
    const { error } = await supabase.rpc('reset_monthly_tryon_credits');
    
    if (error) {
      console.error('[CreditService] Failed to reset monthly credits:', error);
    }
  } catch (error) {
    console.error('[CreditService] Error resetting monthly credits:', error);
  }
}


